import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {zodResolver} from "@hookform/resolvers/zod";
import {useAccount, useCurrentCompany, useSiretLookup} from "@mytour/platform";
import type {CreatePennylaneAccountPayload, SiretLookupResponse} from "@mytour/shared";
import {CreatePennylaneAccountPayloadSchema, SiretLookupSiretSchema} from "@mytour/shared";
import axios from "axios";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {useNavigate} from "react-router";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {useCreatePennylaneAccount, useEinvoicingStatus} from "../query/einvoicingQueries.js";
import {
  PennylaneAccountRecapForm,
  type SiretDiffusionStatus,
  getPennylaneAccountRecapDefaultValues,
} from "./PennylaneAccountRecapForm.js";
import {usePennylaneRecapModal} from "./PennylaneRecapModalContext.js";
import {MilestoneMarker, TINTED_CARD_STYLE} from "./PennylaneStepStyles.js";

// Debounce de la vérification de diffusion Sirene à la saisie du SIRET (mock-up validé #1042).
const SIRET_DIFFUSION_DEBOUNCE_MS = 600;

// Cible du deep link post-création (#651, note de conception du 05/08) :
// ancre vers la section e-invoicing de l'onglet Facturation, où la timeline
// d'activation prend le relais. Même cible que le futur retour de
// redirection Pennylane → MyTour, si #775 le confirme.
const EINVOICING_SECTION_TARGET = "/company-settings/billing#e-invoicing-settings";

/**
 * Modal Récap GLOBALE de création du compte Pennylane (#651) — enregistrée
 * dans le registre `AppModals.tsx`, ouvrable depuis l'écran de choix de
 * Facturation et, plus tard, le nudge #650, via `usePennylaneRecapModal`.
 * `unmountOnClose` (comportement par défaut de reactstrap `Modal`) refait
 * démarrer le formulaire à chaque ouverture avec des valeurs par défaut
 * fraîches — pas de reset manuel nécessaire.
 */
export function PennylaneRecapModal() {
  const {isRecapModalOpen, closeRecapModal} = usePennylaneRecapModal();

  return (
    <Modal isOpen={isRecapModalOpen} toggle={closeRecapModal} centered>
      {isRecapModalOpen && <PennylaneRecapModalContent onClose={closeRecapModal} />}
    </Modal>
  );
}

function PennylaneRecapModalContent({onClose}: {onClose: () => void}) {
  const {company} = useCurrentCompany();
  const legalName = company?.legal.legalName ?? "";
  const {account} = useAccount();
  const {einvoicingStatus, refetchEinvoicingStatus} = useEinvoicingStatus();
  const {createPennylaneAccount, isCreatingPennylaneAccount} = useCreatePennylaneAccount();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Le composant reste monté au changement d'étape (#1100) : `siretDiffusionStatus`,
  // calculé plus bas depuis le SIRET du formulaire, reste donc celui du SIRET
  // soumis au moment de basculer vers "confirmation".
  const [step, setStep] = useState<"form" | "confirmation">("form");
  /**
   * Garde de démontage (#1100) : la fermeture (bouton « Fermer », croix du
   * header, Échap, clic backdrop) démonte ce composant sans annuler l'appel
   * `refetchEinvoicingStatus()` en vol — sans ce ref, sa résolution tardive
   * navigue quand même après une fermeture explicite de l'utilisateur.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Forme canonique de l'idiome (pas juste un cleanup) : un double-montage
    // (StrictMode) réarme `current` à `true`, sinon il resterait figé à
    // `false` après le premier cleanup et le chemin nominal ne navigue plus.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Garde anti double-clic synchrone (#651) : `isCreatingPennylaneAccount` ne
  // se met à jour qu'au prochain rendu React, donc deux clics rapides avant
  // ce rendu passeraient tous les deux le check `disabled`. Un ref mute
  // avant tout appel réseau, indépendamment du cycle de rendu.
  const isSubmittingRef = useRef(false);
  // Pas un champ RHF : jamais soumis au backend (mention légale locale, hors
  // du payload `CreatePennylaneAccountPayloadSchema`), donc un simple gate
  // local plutôt qu'une extension du schéma partagé. Repart à `false` à
  // chaque ouverture — le composant est démonté à la fermeture.
  const [legalRepresentativeConfirmed, setLegalRepresentativeConfirmed] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: {errors},
  } = useForm<CreatePennylaneAccountPayload>({
    resolver: zodResolver(CreatePennylaneAccountPayloadSchema),
    defaultValues: getPennylaneAccountRecapDefaultValues({
      siret: company?.legal.siret ?? null,
      legalName,
      phoneNumber: einvoicingStatus?.phoneNumber ?? null,
    }),
  });
  /**
   * Email affiché en lecture seule (#1099), jamais soumis. Sur un retry, le
   * backend réutilise l'email STOCKÉ de la première tentative (#1107) — la
   * modale l'annonce si présent, sinon l'email du manager connecté (#1106/#1114).
   */
  const storedEmail = einvoicingStatus?.pennylaneUserEmail ?? null;
  const email = storedEmail ?? account?.email ?? "";
  // La checkbox représentant légal suit la raison sociale ÉDITÉE (#1034) — le
  // champ RHF, pas `company.legal.legalName` figé à l'ouverture de la modal.
  const editedLegalName = watch("legalName");
  const siret = watch("siret");
  const {siretDiffusionStatus, isCheckingSiretDiffusion} = useSiretDiffusionCheck(siret);

  const disabled = isCreatingPennylaneAccount;

  if (step === "confirmation") {
    return (
      <>
        <ModalHeader toggle={onClose}>Compte Pennylane créé</ModalHeader>
        <PennylaneAccountConfirmationStep
          siretDiffusionStatus={siretDiffusionStatus}
          onClose={onClose}
          onVerifyAccountActivated={handleVerifyAccountActivated}
        />
      </>
    );
  }

  return (
    <Form onSubmit={handleSubmit(handleConfirm)}>
      <ModalHeader toggle={onClose}>Créer un compte Pennylane</ModalHeader>
      <ModalBody>
        <PennylaneAccountRecapForm
          control={control}
          errors={errors}
          disabled={disabled}
          siretDiffusionStatus={siretDiffusionStatus}
          isCheckingSiretDiffusion={isCheckingSiretDiffusion}
          email={email}
        />
        {submitError && (
          <Alert color="danger" className="mt-3 mb-0">
            {submitError}
          </Alert>
        )}
        <FormGroup check className="mt-3 mb-0">
          <Input
            type="checkbox"
            id="pennylane-legal-representative-confirmed"
            checked={legalRepresentativeConfirmed}
            disabled={disabled}
            onChange={(event) => setLegalRepresentativeConfirmed(event.target.checked)}
          />
          <Label check for="pennylane-legal-representative-confirmed" className="small text-muted">
            Je déclare être le représentant légal de {editedLegalName || legalName} ou disposer
            d'un mandat pour agir en son nom.
          </Label>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={disabled}>
          Annuler
        </Button>
        <Button color="primary" type="submit" disabled={disabled || !legalRepresentativeConfirmed}>
          {disabled && <Spinner size="sm" className="me-2" />}
          Confirmer
        </Button>
      </ModalFooter>
    </Form>
  );

  async function handleConfirm(payload: CreatePennylaneAccountPayload) {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    try {
      setSubmitError(null);
      await createPennylaneAccount(payload);
      setStep("confirmation");
    } catch (error) {
      setSubmitError(resolveErrorMessage(error));
      isSubmittingRef.current = false;
    }
  }

  /**
   * Vérification du clic « Compte activé » (#1100), pattern du jalon 2 —
   * détail dans `docs/context/facturation.md`. `refetch()` react-query ne
   * rejette jamais (pas de `throwOnError`) : sans `result.isError`, un échec
   * réseau se lirait à tort comme « pas encore activé ».
   */
  async function handleVerifyAccountActivated(): Promise<VerificationOutcome> {
    const result = await refetchEinvoicingStatus();
    // `data` peut aussi être absent sans `isError` (fetch dédupliqué par
    // l'invalidation posée par `useCreatePennylaneAccount.onSuccess`) — un
    // statut inconnu n'est pas la preuve d'un compte pas encore activé.
    if (result.isError || result.data === undefined) {
      return "verification_error";
    }
    const isActivated = (result.data?.paRegistrationStatus ?? null) !== null;
    if (isActivated && isMountedRef.current) {
      onClose();
      navigate(EINVOICING_SECTION_TARGET);
    }
    return isActivated ? "activated" : "not_activated";
  }
}

type VerificationOutcome = "activated" | "not_activated" | "verification_error";

type ConfirmationStepProps = {
  siretDiffusionStatus: SiretDiffusionStatus;
  onClose: () => void;
  onVerifyAccountActivated: () => Promise<VerificationOutcome>;
};

const CONFIRMATION_DISCLAIMER_COPY: Record<Exclude<SiretDiffusionStatus, null>, string> = {
  partial:
    "Étant partiellement non diffusible, une action humaine chez Pennylane est nécessaire. Nous vous recommandons de réaliser cette démarche du lundi au vendredi, de 8h30 à 18h30, pour plus de fluidité.",
  notfound:
    "Si votre entreprise est non diffusible, une action humaine chez Pennylane sera nécessaire. Nous vous recommandons de réaliser cette démarche du lundi au vendredi, de 8h30 à 18h30, pour plus de fluidité.",
};

// KO affiché sous le pied de modale (mock-up V10) : "not_activated" reprend le
// conseil d'action de l'utilisateur, "verification_error" ne lui demande rien
// qu'il ait déjà fait — la vérification elle-même a échoué, pas son activation.
const VERIFICATION_FEEDBACK_COPY: Record<"not_activated" | "verification_error", string> = {
  not_activated:
    "Nous ne voyons pas encore votre compte activé. Ouvrez l'email de Pennylane, terminez l'activation et le choix de Pennylane comme Plateforme Agréée, puis réessayez.",
  verification_error: "La vérification a échoué. Réessayez dans quelques instants.",
};

/**
 * Étape de confirmation universelle (#1100, mock-up V10), affichée pour tous
 * les comptes créés. Le disclaimer se greffe selon `siretDiffusionStatus` —
 * affirmatif ("partial"), conditionnel ("notfound", moins probant).
 */
export function PennylaneAccountConfirmationStep({
  siretDiffusionStatus,
  onClose,
  onVerifyAccountActivated,
}: ConfirmationStepProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState<
    "not_activated" | "verification_error" | null
  >(null);

  const disclaimer = siretDiffusionStatus
    ? CONFIRMATION_DISCLAIMER_COPY[siretDiffusionStatus]
    : null;

  return (
    <>
      <ModalBody>
        <div className="d-flex gap-3">
          <MilestoneMarker done>
            <FontAwesomeIcon icon="check" />
          </MilestoneMarker>
          <div>
            <p className="fw-semibold mb-1">Vous allez recevoir un email de Pennylane</p>
            <p className="text-muted small mb-0">
              Ouvrez-le pour activer votre compte et choisir Pennylane comme Plateforme Agréée
              (PA).
            </p>
          </div>
        </div>
        {disclaimer && (
          <div className="mt-3" style={TINTED_CARD_STYLE}>
            <p className="small mb-0">{disclaimer}</p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={isVerifying}>
          Fermer
        </Button>
        <Button color="primary" onClick={handleAccountActivatedClick} disabled={isVerifying}>
          {isVerifying ? "Vérification…" : "Compte activé"}
          {isVerifying && <Spinner size="sm" className="ms-2" />}
        </Button>
      </ModalFooter>
      {/* Sous le pied de modale, pas dans ModalBody (mock-up V10) — même position que
          `refetchedWithoutProgress` du jalon 2 (`EinvoicingPennylaneTunnelState.tsx`). */}
      {verificationFeedback && (
        <p className="text-muted small fst-italic px-3 pb-3 mb-0">
          {VERIFICATION_FEEDBACK_COPY[verificationFeedback]}
        </p>
      )}
    </>
  );

  async function handleAccountActivatedClick() {
    setIsVerifying(true);
    setVerificationFeedback(null);
    const outcome = await onVerifyAccountActivated();
    // Sur "activated", le parent ferme la modale — pas de retour à un état
    // stable ici (le composant se démonte).
    if (outcome !== "activated") {
      setVerificationFeedback(outcome);
      setIsVerifying(false);
    }
  }
}

function resolveErrorMessage(error: unknown): string {
  // 422 = email déjà pris chez Pennylane via un autre canal — le seul cas
  // documenté comme durable, pas transitoire (docs/context/facturation.md).
  if (axios.isAxiosError(error) && error.response?.status === 422) {
    return "Un compte Pennylane existe déjà avec cet email. Contactez le support MyTour.";
  }
  return "La création du compte Pennylane a échoué. Réessayez dans quelques instants.";
}

/**
 * Vérification amont du statut de diffusion Sirene (#1042) : informatif seul,
 * jamais de blocage — réutilise `siret-autofill` (#158). Échec réseau →
 * `siretLookup` reste `undefined`, aucun encart (hook déjà en `retry: false`).
 */
function useSiretDiffusionCheck(siret: string): {
  siretDiffusionStatus: SiretDiffusionStatus;
  isCheckingSiretDiffusion: boolean;
} {
  /**
   * Miroir du trim de `CreatePennylaneAccountPayloadSchema.siret` (#1042) : sans
   * lui, un SIRET collé avec espace passe le formulaire mais saute la vérification.
   */
  const trimmedSiret = (siret ?? "").trim();
  const debouncedSiret = useDebouncedSiret(trimmedSiret, SIRET_DIFFUSION_DEBOUNCE_MS);
  const {siretLookup, isSiretLookupFetching} = useSiretLookup(debouncedSiret, true);
  const isSiretFormatValid = SiretLookupSiretSchema.safeParse(trimmedSiret).success;
  /**
   * Tant que le debounce n'a pas rattrapé la saisie, le résultat en main
   * appartient au SIRET précédent : pas d'encart avant convergence (#1042).
   */
  const isSettled = debouncedSiret === trimmedSiret;

  return {
    siretDiffusionStatus: isSettled ? resolveSiretDiffusionStatus(siretLookup) : null,
    isCheckingSiretDiffusion: isSiretFormatValid && (!isSettled || isSiretLookupFetching),
  };
}

function resolveSiretDiffusionStatus(
  lookup: SiretLookupResponse | undefined
): SiretDiffusionStatus {
  if (!lookup) {
    return null;
  }
  if (lookup.found === false) {
    return "notfound";
  }
  return lookup.data.isDiffusionRestricted ? "partial" : null;
}

/**
 * Debounce local à ce fichier — même pattern que
 * `Step1AutofillForm.useDebouncedSiret` (#158), pas d'extraction en hook
 * partagé pour ce seul deuxième call-site (#1042).
 */
function useDebouncedSiret(siret: string, delayMs: number): string {
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(siret ?? ""), delayMs);
    return () => clearTimeout(timer);
  }, [siret, delayMs]);

  return debounced;
}
